import React, { useState, useEffect, useMemo, useRef } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { FRAGRANCE_FIELDS, isFragrance } from '../utils/fragranceDraft.mjs'
import { useToast } from '../../../lib/ToastContext'
import { printTags } from '../utils/printTag'

const METAFIELD_KEYS = [
  ...Object.keys(FRAGRANCE_FIELDS).filter(key => key !== 'brand'),
  'product_type', 'brand', 'material', 'gender', 'age_group', 'color',
  'size', 'weight', 'weight_unit', 'condition', 'gtin', 'mpn',
  'stone_type', 'stone_carat', 'karat', 'ring_size'
]

const METAFIELD_LABELS = {
  ...FRAGRANCE_FIELDS,
  product_type: 'Product Type', brand: 'Brand', material: 'Material',
  gender: 'Gender', age_group: 'Age Group', color: 'Color',
  size: 'Size', weight: 'Weight', weight_unit: 'Weight Unit',
  condition: 'Condition', gtin: 'GTIN', mpn: 'MPN',
  stone_type: 'Stone Type', stone_carat: 'Stone Carat',
  karat: 'Karat', ring_size: 'Ring Size',
  tag_print_info: 'Tag Print Info'
}

const TAG_PRINT_INFO_MAX = 24

function computeEan8(value) {
  if (!value) return ''
  const digits = String(value).replace(/\D/g, '')
  if (digits.length < 7) return ''
  const base = digits.slice(0, 7)
  const nums = base.split('').map(Number)
  const oddSum = nums[0] + nums[2] + nums[4] + nums[6]
  const evenSum = nums[1] + nums[3] + nums[5]
  const total = oddSum * 3 + evenSum
  const check = (10 - (total % 10)) % 10
  return `${base}${check}`
}

function isPreferredEan8(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length !== 8 || !digits.startsWith('17')) return false
  return computeEan8(digits.slice(0, 7)) === digits
}

function normalizeMoney(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const normalized = raw.replace(',', '.')
  const parsed = Number.parseFloat(normalized)
  if (!Number.isFinite(parsed)) return null
  return Math.round(parsed * 100)
}

function buildFormData(product) {
  const variant = product?.variants?.[0] || {}
  return {
    title: product?.title || '',
    description: product?.bodyHtml || product?.description || '',
    productType: product?.productType || '',
    tags: product?.tags || '',
    price: variant.price || product?.price || '',
    compareAtPrice: variant.compareAtPrice || product?.compareAtPrice || '',
    sku: variant.sku || product?.sku || '',
    barcode: variant.barcode || product?.barcode || '',
    weight: variant.weight || product?.weight || '',
    inventoryQuantity: variant.inventoryQuantity ?? product?.inventoryQuantity ?? '',
    metafields: product?.metafields || {}
  }
}

const ProductEditPanel = ({
  product,
  onUpdatePrice,
  onEnsureBarcode,
  onUpdateInventory,
  onUpdateFields,
  onUpdateImage,
  onRegenerateAI,
  applySuggestionAction,
  tagConfig,
  onPrintTag,
  onClose
}) => {
  const toast = useToast()
  const [formData, setFormData] = useState(() => buildFormData(product))
  const [saving, setSaving] = useState(false)
  const [aiProcessing, setAiProcessing] = useState(false)
  const [showMetafields, setShowMetafields] = useState(false)
  const [updatingQty, setUpdatingQty] = useState(false)
  const [updatingImage, setUpdatingImage] = useState(false)
  const [displayImage, setDisplayImage] = useState(product?.image || '')
  const lastAppliedSuggestionIdRef = useRef(null)
  const imageInputRef = useRef(null)

  const originalData = useMemo(() => buildFormData(product), [product])

  // Reset form when product changes
  useEffect(() => {
    setFormData(buildFormData(product))
    setShowMetafields(false)
    setDisplayImage(product?.image || '')
  }, [product?.id])

  useEffect(() => {
    setDisplayImage(product?.image || '')
  }, [product?.image])

  useEffect(() => {
    const requestId = applySuggestionAction?.requestId
    if (!requestId || requestId === lastAppliedSuggestionIdRef.current) return
    lastAppliedSuggestionIdRef.current = requestId

    const patch = applySuggestionAction?.patch || {}
    setFormData((prev) => {
      const next = { ...prev, metafields: { ...(prev.metafields || {}) } }
      const directPatch = patch.direct || {}
      const metafieldPatch = patch.metafields || {}

      for (const [key, value] of Object.entries(directPatch)) {
        if (value === undefined || value === null) continue
        if (key === 'tags' && Array.isArray(value)) {
          next.tags = value.filter(Boolean).join(', ')
          continue
        }
        next[key] = String(value)
      }

      for (const [key, value] of Object.entries(metafieldPatch)) {
        if (value === undefined || value === null) continue
        if (key === 'tag_print_info') {
          next.metafields[key] = String(value).slice(0, TAG_PRINT_INFO_MAX)
          continue
        }
        next.metafields[key] = String(value)
      }

      return next
    })
  }, [applySuggestionAction])

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData)
  }, [formData, originalData])

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const updateMetafield = (key, value) => {
    setFormData(prev => ({
      ...prev,
      metafields: { ...prev.metafields, [key]: value }
    }))
  }

  const handleSave = async () => {
    if (!isDirty) return
    setSaving(true)
    try {
      if (onUpdateFields) {
        await onUpdateFields(product.id, {
          title: formData.title,
          bodyHtml: formData.description,
          productType: formData.productType,
          tags: formData.tags,
          metafields: formData.metafields
        })
      }

      // Update price if changed
      const variant = product?.variants?.[0] || {}
      const variantId = variant.shopifyGid || variant.id
      if (variantId && (formData.price !== originalData.price || formData.compareAtPrice !== originalData.compareAtPrice)) {
        await onUpdatePrice(product.id, variantId, formData.price, formData.compareAtPrice || undefined)
      }

      // Update barcode if changed
      if (!isFragrance(product) && variantId && formData.barcode !== originalData.barcode && onEnsureBarcode) {
        await onEnsureBarcode(product.id, variantId, formData.sku)
      }

      toast.success('Product updated')
    } catch (err) {
      console.error('Failed to save:', err)
      toast.error('Failed to save: ' + (err.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateQty = async () => {
    const variant = product?.variants?.[0] || {}
    const variantId = variant.shopifyGid || variant.id
    if (!variantId || !onUpdateInventory) return

    const qty = Number(formData.inventoryQuantity)
    if (String(formData.inventoryQuantity).trim() === '' || !Number.isSafeInteger(qty) || qty < 0) {
      toast.warning('Enter a whole number of pieces, zero or more')
      return
    }

    setUpdatingQty(true)
    try {
      const result = await onUpdateInventory(product.id, variantId, qty)
      if (result?.ok !== true) throw new Error('Shopify did not confirm the quantity')
      toast.success('Inventory updated')
    } catch (err) {
      toast.error(err.message || 'Failed to update inventory')
    } finally {
      setUpdatingQty(false)
    }
  }

  const handleAskAI = async () => {
    if (!onRegenerateAI) return
    setAiProcessing(true)
    try {
      const result = await onRegenerateAI(product?.image || null, {
        title: formData.title,
        description: formData.description,
        productType: formData.productType,
        category: product?.category,
        metafields: formData.metafields,
        weight: formData.weight,
        tags: formData.tags,
        price: formData.price
      })
      if (result) {
        setFormData(prev => ({
          ...prev,
          title: result.title || prev.title,
          description: result.description || prev.description,
          productType: result.productType || prev.productType,
          tags: Array.isArray(result.tags) ? result.tags.join(', ') : (result.tags || prev.tags),
          metafields: { ...prev.metafields, ...(result.metafields || {}) }
        }))
        toast.success('AI fields regenerated')
      }
    } catch (err) {
      toast.error('AI regeneration failed')
    } finally {
      setAiProcessing(false)
    }
  }

  const handlePrintTag = async () => {
    const currentPrice = normalizeMoney(formData.price)
    const originalPrice = normalizeMoney(originalData.price)
    const priceUnchanged = currentPrice === originalPrice

    if (priceUnchanged) {
      const shouldPrint = window.confirm('The price is the same. Are you sure you need to print a new tag?')
      if (!shouldPrint) return
    }

    const variant = product?.variants?.[0] || {}
    const variantId = variant.shopifyGid || variant.id
    let barcodeToPrint = String(formData.barcode || '').replace(/\D/g, '')
    let skuToPrint = String(formData.sku || '').trim()

    if (!isFragrance(product) && variantId && onEnsureBarcode && !isPreferredEan8(barcodeToPrint)) {
      try {
        const ensured = await onEnsureBarcode(product.id, variantId, skuToPrint)
        const ensuredBarcode = String(ensured?.barcode || '').replace(/\D/g, '')
        if (isPreferredEan8(ensuredBarcode)) {
          barcodeToPrint = ensuredBarcode
          if (!skuToPrint) skuToPrint = ensuredBarcode
          setFormData((prev) => ({
            ...prev,
            barcode: ensuredBarcode,
            sku: prev.sku || ensuredBarcode
          }))
        }
      } catch (err) {
        console.error('Failed to ensure barcode before printing:', err)
        toast.error('Could not generate valid EAN-8 before printing')
        return
      }
    }

    if (!isPreferredEan8(barcodeToPrint)) {
      toast.warning('This product still has no valid EAN-8 starting with 17.')
      return
    }

    const tagProduct = {
      title: formData.title,
      price: formData.price,
      sku: skuToPrint,
      barcode: barcodeToPrint,
      weight: formData.weight || formData.metafields?.weight || '',
      karat: formData.metafields?.karat || '',
      tagPrintInfo: formData.metafields?.tag_print_info || '',
      productType: formData.productType,
      metafields: formData.metafields
    }
    printTags([tagProduct], tagConfig)
  }

  const handleReset = () => {
    setFormData(buildFormData(product))
  }

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Failed to read image file'))
    reader.readAsDataURL(file)
  })

  const handleChangePicture = async (event) => {
    const file = event?.target?.files?.[0]
    if (event?.target) event.target.value = ''
    if (!file) return

    if (!onUpdateImage) {
      toast.warning('Image update is unavailable right now')
      return
    }
    if (!file.type?.startsWith('image/')) {
      toast.warning('Please select an image file')
      return
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.warning('Image too large. Max size is 8MB.')
      return
    }

    setUpdatingImage(true)
    try {
      const dataUrl = await fileToDataUrl(file)
      const result = await onUpdateImage(product.id, dataUrl, file.name)
      if (result?.imageUrl) {
        setDisplayImage(result.imageUrl)
      }
      toast.success('Product image updated')
    } catch (err) {
      console.error('Failed to update image:', err)
      toast.error(err?.message || 'Failed to update image')
    } finally {
      setUpdatingImage(false)
    }
  }

  const variant = product?.variants?.[0] || {}
  const variants = product?.variants || []
  const totalQty = variants.reduce((sum, v) => sum + (parseInt(v.inventoryQuantity, 10) || 0), 0)

  return (
    <Card variant="solid" className="border border-border/60 shadow-elevation-1 max-h-[calc(100vh-220px)] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-card pb-3 border-b border-border/60 z-10">
        <div className="flex items-center gap-3 min-w-0">
          {product?.image && (
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-border/60 flex-shrink-0">
              <img src={product.image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-semibold text-foreground truncate text-sm">{formData.title || 'Edit Product'}</h3>
            <span className="text-xs text-muted-foreground font-mono">{formData.sku || 'No SKU'}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <Icon name="X" size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <div className="w-full rounded-xl overflow-hidden border border-border/60 bg-muted/20 mb-2">
            {displayImage ? (
              <img
                src={displayImage}
                alt={formData.title || 'Product image'}
                className="w-full h-[220px] object-cover"
              />
            ) : (
              <div className="w-full h-[220px] flex items-center justify-center text-muted-foreground/70">
                <div className="flex flex-col items-center gap-2">
                  <Icon name="Image" size={24} />
                  <span className="text-xs">No image</span>
                </div>
              </div>
            )}
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleChangePicture}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => imageInputRef.current?.click()}
            disabled={updatingImage || !onUpdateImage}
          >
            <Icon name={updatingImage ? "Loader2" : "Upload"} size={14} className={`mr-1 ${updatingImage ? 'animate-spin' : ''}`} />
            {updatingImage ? 'Uploading...' : 'Change Picture'}
          </Button>
        </div>

        {/* Title */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Description (HTML)</label>
          <textarea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        {/* Product Type & Tags */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Product Type</label>
            <input
              type="text"
              value={formData.productType}
              onChange={(e) => updateField('productType', e.target.value)}
              className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Tags</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => updateField('tags', e.target.value)}
              placeholder="comma, separated, tags"
              className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-muted-foreground">Tag Print Info</label>
            <span className="text-[11px] text-muted-foreground">
              {(formData.metafields?.tag_print_info || '').length}/{TAG_PRINT_INFO_MAX}
            </span>
          </div>
          <input
            type="text"
            value={formData.metafields?.tag_print_info || ''}
            onChange={(e) => updateMetafield('tag_print_info', e.target.value.slice(0, TAG_PRINT_INFO_MAX))}
            maxLength={TAG_PRINT_INFO_MAX}
            placeholder="AU14K RING CZ1.2CT 3.5G"
            className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Short line for label print: AU type, item name, stone ct (if any), and weight in grams.
          </p>
        </div>

        {/* Pricing */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Pricing</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => updateField('price', e.target.value)}
                  className="w-full pl-7 pr-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Compare At</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.compareAtPrice}
                  onChange={(e) => updateField('compareAtPrice', e.target.value)}
                  className="w-full pl-7 pr-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Weight (g)</label>
              <input
                type="number"
                step="0.01"
                value={formData.weight}
                onChange={(e) => updateField('weight', e.target.value)}
                className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">SKU</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => updateField('sku', e.target.value)}
                className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
        </div>

        {/* Barcode */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">{isFragrance(product) ? 'Manufacturer barcode (UPC/EAN)' : 'Barcode (EAN-8)'}</label>
            <input
              type="text"
              value={formData.barcode}
              readOnly={isFragrance(product)}
              onChange={(e) => updateField('barcode', e.target.value.replace(/\D/g, '').slice(0, isFragrance(product) ? 14 : 8))}
              className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Inventory</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={formData.inventoryQuantity}
                onChange={(e) => updateField('inventoryQuantity', e.target.value)}
                min="0"
                className="flex-1 px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleUpdateQty}
                disabled={updatingQty}
                title="Update inventory in Shopify"
                className="px-2"
              >
                <Icon name={updatingQty ? "Loader2" : "Save"} size={14} className={updatingQty ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>

        {/* Multi-variant info */}
        {variants.length > 1 && (
          <div className="p-3 bg-muted/30 rounded-lg border border-border/60">
            <p className="text-xs text-muted-foreground mb-2">
              <Icon name="Layers" size={14} className="inline mr-1" />
              {variants.length} variants • Total qty: {totalQty}
            </p>
            <div className="space-y-1">
              {variants.map((v, idx) => (
                <div key={v.shopifyGid || v.id || idx} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{v.title || `Variant ${idx + 1}`}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono">{v.sku || '-'}</span>
                    <span className="font-medium">${parseFloat(v.price || 0).toFixed(2)}</span>
                    <span className={parseInt(v.inventoryQuantity, 10) <= 0 ? 'text-destructive' : ''}>
                      Qty: {v.inventoryQuantity ?? 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metafields (collapsible) */}
        <div className="border border-border/60 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowMetafields(!showMetafields)}
            className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Icon name="EyeOff" size={14} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Metafields</span>
              <span className="text-xs text-muted-foreground">({METAFIELD_KEYS.length} fields)</span>
            </div>
            <Icon name={showMetafields ? "ChevronUp" : "ChevronDown"} size={16} className="text-muted-foreground" />
          </button>

          {showMetafields && (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {METAFIELD_KEYS.filter(key => isFragrance(product) ? !['material','karat','stone_type','stone_carat','ring_size'].includes(key) : !(key in FRAGRANCE_FIELDS && key !== 'brand')).map((key) => (
                <div key={key}>
                  <label className="text-xs text-muted-foreground">{METAFIELD_LABELS[key]}</label>
                  <input
                    type="text"
                    value={formData.metafields[key] || ''}
                    onChange={(e) => updateMetafield(key, e.target.value)}
                    className="w-full px-3 py-1.5 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary/20"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border/60">
          {onRegenerateAI && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAskAI}
              disabled={aiProcessing}
            >
              <Icon name={aiProcessing ? "Loader2" : "Sparkles"} size={14} className={`mr-1 ${aiProcessing ? 'animate-spin' : ''}`} />
              Ask AI
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrintTag}
          >
            <Icon name="Printer" size={14} className="mr-1" />
            Print Tag
          </Button>
          <div className="flex-1" />
          {isDirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            <Icon name={saving ? "Loader2" : "Check"} size={14} className={`mr-1 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default ProductEditPanel
